import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/stores/identity';
import type {
  Schedule,
  Shift,
  Staff,
  Location,
  TimeOffRequest,
} from '@/lib/types';
import { formatDate } from '@/lib/dates';

/**
 * Staff "My Activity" page (Day 15.x — Reporting Phase 1).
 *
 * Personal status view answering four questions:
 *   1. My hours this month (vs target)
 *   2. My upcoming shifts (next 14 days)
 *   3. My time-off (status of recent requests)
 *   4. Schedule status (current + next month publish state)
 *
 * Information without anxiety — Staff should glance and feel reassured, not
 * pulled into operational drama. No comparisons against coworkers, no
 * editorializing about whether a manager is late.
 */

interface MyData {
  staff: Staff | null;
  primaryLocation: Location | null;
  monthShifts: Shift[];
  upcomingShifts: Shift[];
  timeOff: TimeOffRequest[];
  schedules: Schedule[];
}

function shiftHours(s: Shift): number {
  // start_time / end_time are HH:MM:SS strings, all same date.
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60; // handles overnight, just in case
  mins -= s.break_minutes ?? 0;
  return Math.max(0, mins) / 60;
}

function startOfMonth(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function startOfNextMonth(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().slice(0, 10);
}
function endOfMonthIso(d: Date): string {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

/**
 * Convert a per-week or per-month target into the equivalent monthly hours
 * target so the "this month" tile compares apples to apples.
 */
function monthlyTarget(
  perPeriodHours: number,
  period: 'week' | 'month',
  monthStart: Date
): number {
  if (period === 'month') return perPeriodHours;
  // Approximate weeks-in-month by days/7 — good enough for utilization framing.
  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0
  ).getDate();
  return Math.round(perPeriodHours * (daysInMonth / 7));
}

export default function MyActivity() {
  const user = useUser();
  const [status, setStatus] = useState<'loading' | 'loaded' | 'no-staff' | 'error'>(
    'loading'
  );
  const [data, setData] = useState<MyData>({
    staff: null,
    primaryLocation: null,
    monthShifts: [],
    upcomingShifts: [],
    timeOff: [],
    schedules: [],
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) return;
      try {
        // 1. Find this user's staff record via profile_id.
        const { data: staffRows, error: staffErr } = await supabase
          .from('staff')
          .select('*')
          .eq('profile_id', user.id)
          .limit(1);
        if (staffErr) throw staffErr;
        const staff = (staffRows?.[0] ?? null) as Staff | null;
        if (!staff) {
          if (!cancelled) setStatus('no-staff');
          return;
        }

        // 2. Primary location (for schedule status + publish lead-days).
        let primaryLocation: Location | null = null;
        if (staff.primary_location_id) {
          const { data: locRow, error: locErr } = await supabase
            .from('locations')
            .select('*')
            .eq('id', staff.primary_location_id)
            .maybeSingle();
          if (locErr) throw locErr;
          primaryLocation = (locRow ?? null) as Location | null;
        }

        // 3. This month's shifts (for hours total).
        const today = new Date();
        const monthStart = startOfMonth(today);
        const monthEnd = endOfMonthIso(today);
        const { data: monthShifts, error: msErr } = await supabase
          .from('shifts')
          .select('*')
          .eq('staff_id', staff.id)
          .gte('shift_date', monthStart)
          .lte('shift_date', monthEnd)
          .is('cancelled_at', null);
        if (msErr) throw msErr;

        // 4. Upcoming shifts: today through +14 days.
        const todayIso = today.toISOString().slice(0, 10);
        const in14 = new Date(today.getTime() + 14 * 86400000)
          .toISOString()
          .slice(0, 10);
        const { data: upcoming, error: upErr } = await supabase
          .from('shifts')
          .select('*')
          .eq('staff_id', staff.id)
          .gte('shift_date', todayIso)
          .lte('shift_date', in14)
          .is('cancelled_at', null)
          .order('shift_date', { ascending: true })
          .order('start_time', { ascending: true });
        if (upErr) throw upErr;

        // 5. My time-off requests (last 90 days inclusive).
        const ninetyAgo = new Date(today.getTime() - 90 * 86400000)
          .toISOString()
          .slice(0, 10);
        const { data: timeOff, error: toErr } = await supabase
          .from('time_off_requests')
          .select('*')
          .eq('staff_id', staff.id)
          .gte('start_date', ninetyAgo)
          .order('start_date', { ascending: false });
        if (toErr) throw toErr;

        // 6. Schedules at primary location (current + next month).
        let schedules: Schedule[] = [];
        if (primaryLocation) {
          const nextMonthStart = startOfNextMonth(today);
          const { data: schedRows, error: schErr } = await supabase
            .from('schedules')
            .select('*')
            .eq('location_id', primaryLocation.id)
            .in('month_start_date', [monthStart, nextMonthStart]);
          if (schErr) throw schErr;
          schedules = (schedRows ?? []) as Schedule[];
        }

        if (!cancelled) {
          setData({
            staff,
            primaryLocation,
            monthShifts: (monthShifts ?? []) as Shift[],
            upcomingShifts: (upcoming ?? []) as Shift[],
            timeOff: (timeOff ?? []) as TimeOffRequest[],
            schedules,
          });
          setStatus('loaded');
        }
      } catch {
        // Surface via the error state -- the page already renders an error
        // banner when status='error'. err message intentionally not surfaced
        // (page is a personal dashboard; vague is fine).
        if (!cancelled) setStatus('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // ---- derived values ------------------------------------------------------

  const hoursThisMonth = useMemo(
    () => data.monthShifts.reduce((sum, s) => sum + shiftHours(s), 0),
    [data.monthShifts]
  );

  const target = useMemo(() => {
    if (!data.staff) return null;
    const today = new Date();
    return {
      min: monthlyTarget(
        Number(data.staff.min_hours),
        data.staff.hours_target_period,
        today
      ),
      max: monthlyTarget(
        Number(data.staff.max_hours),
        data.staff.hours_target_period,
        today
      ),
    };
  }, [data.staff]);

  const monthStartIso = startOfMonth(new Date());
  const nextMonthIso = startOfNextMonth(new Date());
  const currentSchedule = data.schedules.find(
    (s) => s.month_start_date === monthStartIso
  );
  const nextSchedule = data.schedules.find(
    (s) => s.month_start_date === nextMonthIso
  );

  const publishDeadlineIso = useMemo(() => {
    if (!data.primaryLocation) return null;
    const lead = data.primaryLocation.schedule_publish_lead_days ?? 10;
    const next = new Date(nextMonthIso + 'T00:00:00');
    next.setDate(next.getDate() - lead);
    return next.toISOString().slice(0, 10);
  }, [data.primaryLocation, nextMonthIso]);

  const pendingCount = data.timeOff.filter((r) => r.status === 'pending').length;
  const approvedDays = data.timeOff
    .filter((r) => r.status === 'approved')
    .reduce((sum, r) => {
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      return (
        sum + Math.round((end.getTime() - start.getTime()) / 86400000) + 1
      );
    }, 0);

  // ---- render --------------------------------------------------------------

  if (status === 'loading') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading your activity...
      </div>
    );
  }

  if (status === 'no-staff') {
    return (
      <div className="container mx-auto py-10 px-4 text-center space-y-2">
        <h1 className="text-xl font-semibold">No staff record</h1>
        <p className="text-sm text-muted-foreground">
          You're signed in but you don't have a staff record yet. Ask your
          manager to add you, or accept your invite if you have one pending.
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="container mx-auto py-10 px-4 text-center text-sm text-red-700">
        Couldn't load your activity. Please refresh.
      </div>
    );
  }

  const monthLabel = new Date().toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const nextMonthLabel = new Date(nextMonthIso + 'T00:00:00').toLocaleDateString(
    undefined,
    { month: 'long', year: 'numeric' }
  );

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">My activity</p>
            <h1 className="text-2xl font-semibold tracking-tight">
              {data.staff?.display_name ?? user?.display_name ?? 'My activity'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {data.primaryLocation?.name ?? 'No primary location'} ·{' '}
              {monthLabel}
            </p>
          </div>
          <Link
            to="/my-activity/notifications"
            className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted shrink-0"
          >
            Notification preferences
          </Link>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="Hours this month"
            value={`${hoursThisMonth.toFixed(0)} h`}
            sub={
              target
                ? `${Math.round((hoursThisMonth / Math.max(target.max, 1)) * 100)}% of max`
                : undefined
            }
          />
          <KpiCard
            label="Next shift"
            value={data.upcomingShifts[0] ? formatDate(data.upcomingShifts[0].shift_date) : '—'}
            sub={
              data.upcomingShifts[0]
                ? `${data.upcomingShifts[0].start_time.slice(0, 5)}–${data.upcomingShifts[0].end_time.slice(0, 5)}`
                : 'None scheduled'
            }
            tone="positive"
          />
          <KpiCard
            label="Time-off pending"
            value={String(pendingCount)}
            sub={pendingCount > 0 ? 'Awaiting decision' : 'All decided'}
            tone={pendingCount > 0 ? 'warning' : 'neutral'}
          />
          <KpiCard
            label={`${nextMonthLabel} schedule`}
            value={
              nextSchedule
                ? nextSchedule.status === 'published'
                  ? 'Published'
                  : nextSchedule.status === 'closed'
                    ? 'Closed'
                    : 'Draft'
                : 'Not started'
            }
            sub={
              publishDeadlineIso
                ? `Posts by ${formatDate(publishDeadlineIso)}`
                : undefined
            }
          />
        </div>

        {/* Tile grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 01 — Hours this month */}
          <Tile id="01" label="Hours" title="My hours this month">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-semibold">
                {hoursThisMonth.toFixed(0)}
              </span>
              <span className="text-sm text-muted-foreground">
                / {target?.max.toFixed(0) ?? '—'} h target
              </span>
            </div>
            <ProgressBar
              value={hoursThisMonth}
              min={target?.min ?? 0}
              max={Math.max(target?.max ?? 1, 1)}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Min {target?.min.toFixed(0) ?? '—'} h</span>
              <span>Max {target?.max.toFixed(0) ?? '—'} h</span>
            </div>
            {target && hoursThisMonth >= target.min && hoursThisMonth <= target.max && (
              <p className="text-xs text-emerald-700 mt-2">
                On track — {(target.max - hoursThisMonth).toFixed(0)} hours below your max.
              </p>
            )}
            {target && hoursThisMonth < target.min && (
              <p className="text-xs text-amber-700 mt-2">
                Below your minimum target. Talk to your manager if this is unexpected.
              </p>
            )}
            {target && hoursThisMonth > target.max && (
              <p className="text-xs text-red-700 mt-2">
                Above your maximum target.
              </p>
            )}
          </Tile>

          {/* 02 — Upcoming shifts */}
          <Tile id="02" label="Schedule" title="My upcoming shifts">
            {data.upcomingShifts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No shifts scheduled in the next 14 days.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.upcomingShifts.slice(0, 5).map((s, i) => (
                  <li
                    key={s.id}
                    className={`flex justify-between items-center px-3 py-2 rounded-md ${
                      i === 0 ? 'bg-muted/60' : ''
                    }`}
                  >
                    <div>
                      <div className="font-medium">
                        {formatDate(s.shift_date)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {shiftHours(s).toFixed(0)} h
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Tile>

          {/* 03 — My time-off */}
          <Tile id="03" label="Time-off" title="My time-off">
            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <Stat label="Pending" value={pendingCount.toString()} tone="warning" />
              <Stat label="Approved days" value={approvedDays.toString()} tone="positive" />
              <Stat
                label="Denied"
                value={data.timeOff.filter((r) => r.status === 'denied').length.toString()}
              />
            </div>
            {data.timeOff.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No requests in the last 90 days.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {data.timeOff.slice(0, 4).map((r) => (
                  <li key={r.id} className="flex justify-between">
                    <span>
                      {formatDate(r.start_date)}
                      {r.start_date !== r.end_date && ` – ${formatDate(r.end_date)}`}
                    </span>
                    <StatusPill status={r.status} />
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/time-off/new"
              className="inline-block mt-3 text-xs text-primary underline"
            >
              Request time off →
            </Link>
          </Tile>

          {/* 04 — Schedule status */}
          <Tile id="04" label="Status" title="Schedule status">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center px-3 py-2 rounded-md bg-emerald-50">
                <span>{monthLabel}</span>
                <ScheduleStatusPill schedule={currentSchedule} />
              </div>
              <div className="flex justify-between items-center px-3 py-2 rounded-md bg-muted/40">
                <span>{nextMonthLabel}</span>
                <ScheduleStatusPill schedule={nextSchedule} />
              </div>
              {publishDeadlineIso && nextSchedule?.status !== 'published' && (
                <p className="text-xs text-muted-foreground pt-1">
                  You'll be notified when {nextMonthLabel} goes live. Posts by{' '}
                  {formatDate(publishDeadlineIso)}.
                </p>
              )}
            </div>
          </Tile>
        </div>
      </div>
    </div>
  );
}

// ---------- subcomponents ---------------------------------------------------

function KpiCard({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'positive' | 'warning';
}) {
  const toneClasses =
    tone === 'positive'
      ? 'bg-emerald-50 text-emerald-900'
      : tone === 'warning'
        ? 'bg-amber-50 text-amber-900'
        : 'bg-muted/40';
  return (
    <div className={`rounded-md px-3 py-2 ${toneClasses}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Tile({
  id,
  label,
  title,
  children,
}: {
  id: string;
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {id} · {label}
        </span>
      </div>
      <h2 className="text-sm font-semibold">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}) {
  const valueColor =
    tone === 'positive'
      ? 'text-emerald-700'
      : tone === 'warning'
        ? 'text-amber-700'
        : 'text-foreground';
  return (
    <div className="rounded-md bg-muted/40 px-2 py-1.5">
      <p className={`text-lg font-semibold ${valueColor}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function ProgressBar({ value, min, max }: { value: number; min: number; max: number }) {
  const pct = Math.min(100, (value / Math.max(max, 1)) * 100);
  const minPct = Math.min(100, (min / Math.max(max, 1)) * 100);
  return (
    <div className="relative h-2 bg-muted rounded">
      {/* min target indicator */}
      <div
        className="absolute top-0 bottom-0 bg-amber-200/60"
        style={{ width: `${minPct}%` }}
      />
      {/* scheduled hours */}
      <div
        className="absolute top-0 bottom-0 bg-emerald-600 rounded"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function StatusPill({ status }: { status: TimeOffRequest['status'] }) {
  const cfg: Record<TimeOffRequest['status'], { bg: string; fg: string; label: string }> = {
    pending: { bg: 'bg-amber-100', fg: 'text-amber-800', label: 'pending' },
    approved: { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'approved' },
    denied: { bg: 'bg-red-100', fg: 'text-red-800', label: 'denied' },
    withdrawn: { bg: 'bg-muted', fg: 'text-muted-foreground', label: 'withdrawn' },
    cancelled: { bg: 'bg-muted', fg: 'text-muted-foreground', label: 'cancelled' },
  };
  const c = cfg[status];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${c.bg} ${c.fg}`}>
      {c.label}
    </span>
  );
}

function ScheduleStatusPill({ schedule }: { schedule: Schedule | undefined }) {
  if (!schedule) {
    return (
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        Not started
      </span>
    );
  }
  const cfg: Record<Schedule['status'], { bg: string; fg: string }> = {
    published: { bg: 'bg-emerald-600', fg: 'text-white' },
    draft: { bg: 'bg-amber-600', fg: 'text-white' },
    closed: { bg: 'bg-muted-foreground', fg: 'text-white' },
  };
  const c = cfg[schedule.status];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${c.bg} ${c.fg}`}>
      {schedule.status}
    </span>
  );
}
